import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { TopBanner } from '../components/TopBanner';
import { Box } from '@mui/material';
import { BannerText } from '../types/bannerText';
import api from '../utils/api';

interface BannerContextType {
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
  bannerTexts: BannerText[];
  isLoading: boolean;
  error: Error | null;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error('useBanner must be used within a BannerProvider');
  }
  return context;
};

interface BannerProviderProps {
  children: ReactNode;
}

export const BannerProvider: React.FC<BannerProviderProps> = ({ children }) => {
  const [showBanner, setShowBanner] = useState(true);
  const [bannerTexts, setBannerTexts] = useState<BannerText[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBannerTexts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get<BannerText[]>('/banner-texts/active');
      setBannerTexts(response.data);
      setError(null);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching banner texts:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBannerTexts();
  }, [fetchBannerTexts]);

  const contextValue = useMemo(() => ({
    showBanner,
    setShowBanner,
    bannerTexts,
    isLoading,
    error
  }), [showBanner, bannerTexts, isLoading, error]);

  return (
    <BannerContext.Provider value={contextValue}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {showBanner && (
          <TopBanner onClose={() => setShowBanner(false)} />
        )}
        {children}
      </Box>
    </BannerContext.Provider>
  );
}; 